import moment from 'moment';
import * as React from 'react';
import DatePicker from 'react-datepicker';
import classNames from 'classnames';
import { DateFormat } from 'stageclip-common';
import { omit } from 'lodash';

import { formatTimezoneLabel } from 'helpers/dateHelpers';
import styles from './appDateTimeField.module.scss';
import { AppFormField, AppFormFieldProps, AppFormFieldOwnProps } from './appFormField';

export interface AppDateTimeFieldOwnProps extends AppFormFieldOwnProps {
  timezone: string;
  onChangeCallback?: (value: any) => void;
  autoFocus?: boolean;
  className?: string;
  dateFormat?: DateFormat;
  dst: boolean;
  isTimeSelect: boolean;
}

export class AppDateTimeField extends AppFormField<AppDateTimeFieldOwnProps> {
  protected renderInput(field: AppFormFieldProps): JSX.Element {
    const janOffset = moment
      .tz(field.input.value, this.props.timezone)
      .set({ month: 0, day: 1 })
      .utcOffset();
    const julOffset = moment
      .tz(field.input.value, this.props.timezone)
      .set({ month: 6, day: 1 })
      .utcOffset();
    const utcOffset = Math.min(janOffset, julOffset);

    function onChange(timezone: any, onChangeCallback: (value: any) => void, dst: boolean) {
      return (value: any) => {
        if (value != null) {
          const timeWithoutTimezone = moment(value).format(DateFormat.DateTime);
          let timeToDisplay;

          if (dst) {
            timeToDisplay = moment.tz(timeWithoutTimezone, timezone).format(DateFormat.DateTimeWithTimezone);
          } else {
            timeToDisplay = moment(timeWithoutTimezone)
              .utcOffset(utcOffset, true)
              .format(DateFormat.DateTimeWithTimezone);
          }
          field.input.onChange(timeToDisplay);

          if (onChangeCallback) {
            onChangeCallback(timeToDisplay);
          }
        } else {
          field.input.onChange(null);
        }
      };
    }

    const getSelected = () => {
      if (!field.input.value) return undefined;

      return this.props.dst
        ? moment(field.input.value).tz(this.props.timezone)
        : moment(field.input.value).utcOffset(utcOffset);
    };

    return (
      <div className={styles.dateTimeField}>
        <DatePicker
          {...omit(field.input, ['value'])}
          selected={getSelected()}
          className={classNames('text-input', 'form-control', this.props.className, {
            invalid: this.isFieldInvalid(field),
          })}
          onChange={onChange(this.props.timezone, this.props.onChangeCallback, this.props.dst)}
          onBlur={() => this.props.isTimeSelect && field.input.onBlur(field.input.value)}
          showTimeSelect={this.props.isTimeSelect}
          timeFormat="HH:mm"
          timeIntervals={15}
          dateFormat={this.props.dateFormat || DateFormat.DateTimeWithTimezoneUTC}
          timeCaption="time"
          autoFocus={this.props.autoFocus}
          autoComplete="off"
        />
        {this.props.isTimeSelect && <p className={styles.timezone}>{formatTimezoneLabel(this.props.timezone)}</p>}
      </div>
    );
  }
}
